import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useCurrency } from '../../../../hooks/useCurrency';

const PracticeInformationSection = ({ user, onEdit }) => {
  const intl = useIntl();
  const { symbol: currencySym } = useCurrency();

  const formatNumber = (value) => {
    if (!value || value === 0) return '0';
    return value.toLocaleString();
  };

  const formatAUM = (value) => {
    if (!value || value === 0) return `${currencySym}0`;
    const num = parseFloat(value);
    if (num >= 1000000) {
      return `${currencySym}${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${currencySym}${(num / 1000).toFixed(0)}k`;
    }
    return `${currencySym}${num.toFixed(0)}`;
  };

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-xl font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.sections.practice_information.title' })}</h3>
        <button
          onClick={onEdit}
          className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-succession-bg-primary rounded-lg p-4 border border-succession-border">
          <div className="flex items-center text-succession-text-muted text-sm mb-1">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.sections.practice_information.number_of_clients' })}
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">
            {formatNumber(user.number_of_clients)}
          </div>
        </div>
        <div className="bg-succession-bg-primary rounded-lg p-4 border border-succession-border">
          <div className="flex items-center text-succession-text-muted text-sm mb-1">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.sections.practice_information.assets_under_management' })}
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">
            {formatAUM(user.assets_under_management)}
          </div>
        </div>
        <div className="bg-succession-bg-primary rounded-lg p-4 border border-succession-border">
          <div className="flex items-center text-succession-text-muted text-sm mb-1">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.sections.practice_information.years_practicing' })}
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">
            {user.years_practicing || 0}
          </div>
        </div>
        <div className="bg-succession-bg-primary rounded-lg p-4 border border-succession-border">
          <div className="flex items-center text-succession-text-muted text-sm mb-1">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.sections.practice_information.avg_client_age' })}
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">
            {user.avg_client_age || 0}
          </div>
        </div>
      </div>
    </div>
  );
};

PracticeInformationSection.propTypes = {
  user: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default PracticeInformationSection;
